<script>
	import { onMount } from "svelte";
	import { fetchStoreSettings, getApiBase } from "../services/api";

	export let onPrintSample = () => {};

	let store = null;
	let isFixingSpooler = false;
	let spoolerFeedback = "";
	let selectedPrinter = "";
	let printersList = [];
	let tablePrintDestination = "kitchen";

	// Opções de Impressão
	let autoPrint = true;
	let silentPrint = true;
	let autoCut = true;
	let doubleReceipt = true;
	let printPixQr = true;

	// Status do Servidor
	let isServerOnline = false;
	let serverStatusText = "Detectando impressoras...";
	let isDetecting = false;
	let isTestingPrint = false;

	onMount(async () => {
		// 1. Carrega preferências salvas
		try {
			const savedDest = localStorage.getItem("matafome_table_print_dest");
			if (savedDest && ["kitchen", "cashier", "both"].includes(savedDest)) {
				tablePrintDestination = savedDest;
			}

			const savedPrinter = localStorage.getItem("matafome_selected_printer");
			if (savedPrinter) selectedPrinter = savedPrinter;

			autoPrint = localStorage.getItem("matafome_auto_print") !== "false";
			silentPrint = localStorage.getItem("matafome_silent_print") !== "false";
			autoCut = localStorage.getItem("matafome_auto_cut") !== "false";
			const savedMode = localStorage.getItem("matafome_print_mode");
			if (savedMode) {
				doubleReceipt = savedMode === "double";
			} else {
				doubleReceipt = localStorage.getItem("matafome_double_receipt") !== "false";
			}
			printPixQr = localStorage.getItem("matafome_print_pix_qr") !== "false";
		} catch (e) {}

		// 2. Busca lista real de impressoras do Windows
		await detectPrinters();

		// 3. Dados da loja para preview
		try {
			store = await fetchStoreSettings();
		} catch (e) {
			console.warn("Falha ao carregar dados da loja para o preview:", e);
		}
	});

	async function detectPrinters() {
		isDetecting = true;
		try {
			// Estratégia A: Electron Nativo (mais confiável e rápido)
			if (typeof window !== "undefined" && window.desktop?.getPrinters) {
				const nativeList = await window.desktop.getPrinters();
				if (Array.isArray(nativeList) && nativeList.length > 0) {
					printersList = nativeList.map(p => ({
						name: p.name,
						type: p.isDefault ? "Padrão do Windows" : "Instalada",
						isDefault: !!p.isDefault
					}));
					isServerOnline = true;
					serverStatusText = "Electron Nativo (Silencioso)";
					syncSelectedPrinter();
					return;
				}
			}

			// Estratégia B: Servidor Local HTTP na porta 5000 (Impressora_MataFome.exe)
			const res = await fetch("http://127.0.0.1:5000/list", { signal: AbortSignal.timeout(3000) });
			if (res.ok) {
				const data = await res.json();
				const list = data.printers || [];
				printersList = list.map(name => ({
					name: typeof name === "string" ? name : name.name,
					type: "Porta 5000 (Local)",
					isDefault: false
				}));
				isServerOnline = true;
				serverStatusText = "Servidor Local (Porta 5000)";
				syncSelectedPrinter();
				return;
			}
		} catch (err) {
			console.warn("[MataFome Impressão] Não foi possível obter impressoras automaticamente:", err.message);
		} finally {
			isDetecting = false;
		}

		// Fallback se não detectar
		isServerOnline = false;
		serverStatusText = "Desconectado (Inicie o MataFome Desktop)";
	}

	function syncSelectedPrinter() {
		if (printersList.length === 0) return;
		const exists = printersList.some(p => p.name === selectedPrinter);
		if (!exists) {
			const defaultPrn = printersList.find(p => p.isDefault) || printersList[0];
			selectedPrinter = defaultPrn.name;
			try {
				localStorage.setItem("matafome_selected_printer", selectedPrinter);
			} catch (_) {}
		}
	}

	function handlePrinterSelectChange(e) {
		selectedPrinter = e.target.value;
		try {
			localStorage.setItem("matafome_selected_printer", selectedPrinter);
		} catch (_) {}
	}

	function handleTablePrintDestChange(dest) {
		if (typeof dest === "object" && dest?.target) {
			tablePrintDestination = dest.target.value;
		} else if (typeof dest === "string") {
			tablePrintDestination = dest;
		}
		try {
			localStorage.setItem("matafome_table_print_dest", tablePrintDestination);
		} catch (err) {}
	}

	function handleCheckboxChange(key, value) {
		try {
			localStorage.setItem(key, value ? "true" : "false");
			if (key === "matafome_double_receipt") {
				localStorage.setItem("matafome_print_mode", value ? "double" : "single");
			}
		} catch (_) {}
	}

	async function handleFixSpooler() {
		isFixingSpooler = true;
		spoolerFeedback = "Iniciando diagnóstico e reparo do Windows Spooler...\n1. Parando serviço 'spooler'...";

		try {
			if (typeof window !== "undefined" && window.desktop?.clearSpooler) {
				const res = await window.desktop.clearSpooler();
				if (res.success) {
					spoolerFeedback = `Sucesso: ${res.message}\n• Arquivos travados removidos: ${res.deletedFilesCount || 0}\n• O Windows Spooler foi reiniciado e as impressões travadas foram liberadas.`;
				} else {
					spoolerFeedback = `Resultado: ${res.message}\n(Dica: Se o erro for de permissão, execute o MataFome como Administrador para gerenciar o Spooler do Windows).`;
				}
			} else {
				// Fallback em navegador
				await new Promise(r => setTimeout(r, 800));
				spoolerFeedback = "Procedimento executado. Para esvaziamento físico profundo de pastas do Windows, utilize o aplicativo MataFome Desktop instalado.";
			}
		} catch (err) {
			spoolerFeedback = `Erro ao reparar Spooler: ${err.message}`;
		} finally {
			isFixingSpooler = false;
			await detectPrinters();
		}
	}

	async function handlePrintTest() {
		isTestingPrint = true;
		try {
			const storeSlug = store?.slug || "cardapio";
			const testMode = doubleReceipt ? "double" : "single";
			const testUrl = `${getApiBase()}/api/public/${storeSlug}/receipt/sample?auto=true&mode=${testMode}&width=80`;

			if (typeof window !== "undefined" && window.desktop?.printSilent && silentPrint) {
				try {
					spoolerFeedback = `Enviando teste de impressão diretamente para "${selectedPrinter || 'Impressora Padrão'}"...`;
					const res = await window.desktop.printSilent({
						url: testUrl,
						deviceName: selectedPrinter
					});
					if (res.success) {
						spoolerFeedback = `Teste de impressão enviado com sucesso para a impressora "${selectedPrinter || 'Padrão'}"!`;
					} else {
						spoolerFeedback = `Não foi possível enviar o teste para a impressora: ${res.failureReason || 'Erro desconhecido'}`;
					}
					return;
				} catch (err) {
					console.warn("Erro no teste de impressão silenciosa:", err);
				}
			}

			// Fallback se não estiver no desktop ou se o modo silencioso estiver desligado
			if (onPrintSample) {
				onPrintSample();
			}
		} finally {
			isTestingPrint = false;
		}
	}
</script>

<div class="printer-page">
	<!-- 1. Header Principal -->
	<header class="view-header">
		<div class="header-left">
			<div class="header-badge-row">
				<span class="module-badge">HARDWARE & PERIFÉRICOS</span>
				<span class="device-pill">
					<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
						<polyline points="6 9 6 2 18 2 18 9"></polyline>
						<path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
						<rect x="6" y="14" width="12" height="8"></rect>
					</svg>
					Bobinas 80mm / 58mm
				</span>
			</div>
			<div class="title-wrapper">
				<h1 class="view-title">Central de Impressão Térmica</h1>
				<p class="view-subtitle">Configure impressoras térmicas, roteamento de comandas da cozinha e impressão direta na bobina.</p>
			</div>
		</div>

		<div class="header-actions">
			<!-- Status Pill com Efeito de Pulso -->
			<div class="server-status-pill {isServerOnline ? 'online' : 'offline'}" title="Canal ativo: {serverStatusText}">
				<span class="status-pulse-dot"></span>
				<div class="status-text-group">
					<span class="status-main">{isServerOnline ? 'Impressão Online' : 'Serviço Desconectado'}</span>
					<span class="status-sub">{serverStatusText}</span>
				</div>
			</div>

			<!-- Botão de Atualização Rápida -->
			<button
				type="button"
				class="btn-refresh"
				class:spinning={isDetecting}
				on:click={detectPrinters}
				disabled={isDetecting}
				title="Detectar impressoras conectadas via USB ou Rede"
			>
				<svg class="icon-spin" class:active={isDetecting} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round">
					<polyline points="23 4 23 10 17 10"></polyline>
					<polyline points="1 20 1 14 7 14"></polyline>
					<path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
				</svg>
				<span>{isDetecting ? 'Detectando...' : 'Buscar Dispositivos'}</span>
			</button>
		</div>
	</header>

	<!-- 2. Grid de Conteúdo -->
	<div class="printer-grid">
		<!-- Coluna Esquerda: Configuração, Roteamento, Opções e Diagnóstico -->
		<div class="config-col">
			<!-- Card 1: Seleção de Impressora e Destino de Mesa -->
			<section class="card settings-card">
				<div class="card-title-row">
					<div class="card-icon-box orange">
						<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
							<polyline points="6 9 6 2 18 2 18 9"></polyline>
							<path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
							<rect x="6" y="14" width="12" height="8"></rect>
						</svg>
					</div>
					<div>
						<h3>Impressora de Comandas</h3>
						<span class="card-desc">Dispositivo padrão do sistema operacional para envio de pedidos</span>
					</div>
				</div>

				<div class="form-group">
					<label for="printer-select" class="field-label">Impressora Ativa para Cozinha & Balcão</label>
					<div class="select-wrapper">
						<select id="printer-select" class="form-select" bind:value={selectedPrinter} on:change={handlePrinterSelectChange}>
							{#if printersList.length === 0}
								<option value="">Nenhuma impressora detectada no sistema</option>
							{:else}
								{#each printersList as p}
									<option value={p.name}>{p.name} ({p.type})</option>
								{/each}
							{/if}
						</select>
						<div class="select-chevron">
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
								<polyline points="6 9 12 15 18 9"></polyline>
							</svg>
						</div>
					</div>

					<div class="field-hint-box">
						{#if selectedPrinter}
							<span class="hint-badge connected">
								<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
									<polyline points="20 6 9 17 4 12"></polyline>
								</svg>
								Conectado: <strong>{selectedPrinter}</strong>
							</span>
						{:else}
							<span class="hint-badge warning">
								Selecione a impressora térmica conectada via USB ou Rede.
							</span>
						{/if}
					</div>
				</div>

				<!-- Destino Padrão para Pedidos de Mesa (Salão) -->
				<div class="form-group destination-group">
					<label class="field-label">Destino Padrão para Pedidos de Mesa (Salão)</label>
					<div class="dest-cards-grid">
						<!-- Cozinha -->
						<button
							type="button"
							class="dest-card {tablePrintDestination === 'kitchen' ? 'active' : ''}"
							on:click={() => handleTablePrintDestChange('kitchen')}
						>
							<div class="dest-radio-circle"></div>
							<div class="dest-content">
								<span class="dest-title">Cozinha</span>
								<span class="dest-desc">Apenas produção & preparo</span>
							</div>
						</button>

						<!-- Caixa & Balcão -->
						<button
							type="button"
							class="dest-card {tablePrintDestination === 'cashier' ? 'active' : ''}"
							on:click={() => handleTablePrintDestChange('cashier')}
						>
							<div class="dest-radio-circle"></div>
							<div class="dest-content">
								<span class="dest-title">Caixa</span>
								<span class="dest-desc">Apenas conferência & bar</span>
							</div>
						</button>

						<!-- Ambos -->
						<button
							type="button"
							class="dest-card {tablePrintDestination === 'both' ? 'active' : ''}"
							on:click={() => handleTablePrintDestChange('both')}
						>
							<div class="dest-radio-circle"></div>
							<div class="dest-content">
								<span class="dest-title">Ambos</span>
								<span class="dest-desc">Via Dupla: Cozinha + Caixa</span>
							</div>
						</button>
					</div>
					<span class="field-footnote">Define para onde os cupons de lançamento de mesa serão enviados por padrão. O garçom também pode alternar no lançamento.</span>
				</div>
			</section>

			<!-- Card 2: Opções Operacionais de Impressão -->
			<section class="card settings-card">
				<div class="card-title-row">
					<div class="card-icon-box blue">
						<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
							<circle cx="12" cy="12" r="3"></circle>
							<path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
						</svg>
					</div>
					<div>
						<h3>Opções Operacionais de Impressão</h3>
						<span class="card-desc">Parâmetros de automação, corte e formato das comandas</span>
					</div>
				</div>

				<!-- Destaque: Modo Silencioso (Direto na Bobina) -->
				<div class="highlight-option-card">
					<div class="opt-text-block">
						<div class="opt-title-line">
							<span class="opt-main-title">Modo Silencioso (Direto na Bobina)</span>
							<span class="tag-fast">⚡ Alta Velocidade</span>
						</div>
						<p class="opt-explanation">Dispara a comanda diretamente para a impressora térmica sem abrir nenhuma janela ou diálogo do Windows.</p>
					</div>
					<label class="switch-control">
						<input
							type="checkbox"
							bind:checked={silentPrint}
							on:change={() => handleCheckboxChange("matafome_silent_print", silentPrint)}
						/>
						<span class="slider"></span>
					</label>
				</div>

				<!-- Lista de Switches Secundários -->
				<div class="switches-list">
					<!-- Impressão Automática -->
					<div class="switch-row">
						<div class="switch-info">
							<span class="switch-title">Impressão Automática</span>
							<span class="switch-desc">Imprime imediatamente após confirmação de novo pedido no WhatsApp & Cardápio</span>
						</div>
						<label class="switch-control">
							<input
								type="checkbox"
								bind:checked={autoPrint}
								on:change={() => handleCheckboxChange("matafome_auto_print", autoPrint)}
							/>
							<span class="slider"></span>
						</label>
					</div>

					<!-- Corte de Guilhotina -->
					<div class="switch-row">
						<div class="switch-info">
							<span class="switch-title">Acionamento de Guilhotina (Auto-Cut)</span>
							<span class="switch-desc">Aciona o corte automático da bobina ao término de cada comanda</span>
						</div>
						<label class="switch-control">
							<input
								type="checkbox"
								bind:checked={autoCut}
								on:change={() => handleCheckboxChange("matafome_auto_cut", autoCut)}
							/>
							<span class="slider"></span>
						</label>
					</div>

					<!-- Via Dupla -->
					<div class="switch-row">
						<div class="switch-info">
							<span class="switch-title">Via Dupla de Produção</span>
							<span class="switch-desc">Emite 1x comanda de cozinha (sem valores) + 1x comanda de entrega/caixa (completa)</span>
						</div>
						<label class="switch-control">
							<input
								type="checkbox"
								bind:checked={doubleReceipt}
								on:change={() => handleCheckboxChange("matafome_double_receipt", doubleReceipt)}
							/>
							<span class="slider"></span>
						</label>
					</div>

					<!-- PIX no Cupom -->
					<div class="switch-row">
						<div class="switch-info">
							<span class="switch-title">Chave & QR Code PIX no Cupom</span>
							<span class="switch-desc">Imprime dados bancários e chave PIX diretamente na comanda de entrega</span>
						</div>
						<label class="switch-control">
							<input
								type="checkbox"
								bind:checked={printPixQr}
								on:change={() => handleCheckboxChange("matafome_print_pix_qr", printPixQr)}
							/>
							<span class="slider"></span>
						</label>
					</div>
				</div>

				<!-- Botão de Ação Primário: Testar Impressão -->
				<div class="test-action-box">
					<button
						type="button"
						class="btn-test-print"
						class:loading={isTestingPrint}
						on:click={handlePrintTest}
						disabled={isTestingPrint}
					>
						<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
							<polyline points="6 9 6 2 18 2 18 9"></polyline>
							<path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
							<rect x="6" y="14" width="12" height="8"></rect>
						</svg>
						<span>{isTestingPrint ? 'Enviando Comanda...' : `Enviar Comanda de Teste para "${selectedPrinter || 'Impressora Padrão'}"`}</span>
					</button>
				</div>
			</section>

			<!-- Card 3: Diagnóstico e Reparo do Spooler do Windows -->
			<section class="card diagnostic-card">
				<div class="diag-header-row">
					<div class="diag-icon-box">
						<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
							<path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
						</svg>
					</div>
					<div class="diag-title-content">
						<h4>Diagnóstico do Spooler do Windows</h4>
						<p class="diag-desc">Desengasgue a fila de impressão caso a impressora trave, falte papel ou pare de responder.</p>
					</div>
				</div>

				<div class="diag-action-row">
					<button
						type="button"
						class="btn-repair"
						class:fixing={isFixingSpooler}
						on:click={handleFixSpooler}
						disabled={isFixingSpooler}
					>
						<svg class="repair-icon" class:spin={isFixingSpooler} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
							<polyline points="23 4 23 10 17 10"></polyline>
							<polyline points="1 20 1 14 7 14"></polyline>
							<path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
						</svg>
						<span>{isFixingSpooler ? "Reparando fila de impressão..." : "Limpar Fila do Windows (1-Clique)"}</span>
					</button>
				</div>

				{#if spoolerFeedback}
					<div class="spooler-log-console">
						<div class="console-header">
							<span class="console-dot"></span>
							<span class="console-title">Log de Diagnóstico & Status</span>
						</div>
						<pre class="console-text">{spoolerFeedback}</pre>
					</div>
				{/if}
			</section>
		</div>

		<!-- Coluna Direita: Preview Realista da Comanda Térmica (80mm) -->
		<aside class="preview-col">
			<div class="preview-title-bar">
				<div class="preview-title-group">
					<span class="preview-tag">BOBINA 80MM</span>
					<h3>Prévia da Comanda</h3>
				</div>
				<span class="preview-live-badge">Dados Reais da Loja</span>
			</div>

			<!-- Bobina Térmica com Textura de Papel e Serrilhado -->
			<div class="thermal-receipt-container">
				<!-- Serrilha Superior -->
				<div class="receipt-edge top"></div>

				<div class="thermal-receipt">
					<!-- Cabeçalho do Estabelecimento -->
					<div class="receipt-header">
						<div class="receipt-logo">{store?.name || 'MataFome Gourmet'}</div>
						<div class="receipt-info">{store?.address || 'Av. Principal, 1000 - Delivery'}</div>
						{#if store?.cnpj}
							<div class="receipt-info">CNPJ: {store.cnpj}</div>
						{/if}
						{#if store?.phone}
							<div class="receipt-info">Tel/WhatsApp: {store.phone}</div>
						{/if}
						<div class="divider-dash">--------------------------------</div>
						<div class="receipt-order-id">PEDIDO #TESTE</div>
						<div class="receipt-date">{new Date().toLocaleDateString('pt-BR')} às {new Date().toLocaleTimeString('pt-BR').slice(0, 5)}</div>
						<div class="divider-dash">--------------------------------</div>
					</div>

					<!-- Dados do Cliente & Expedição -->
					<div class="receipt-client">
						<div><strong>Cliente:</strong> {store?.name ? store.name : 'Cliente Exemplo'}</div>
						<div><strong>Telefone:</strong> {store?.phone || '(11) 90000-0000'}</div>
						<div><strong>Entrega:</strong> Balcão / Retirada / Delivery</div>
					</div>

					<div class="divider-dash">--------------------------------</div>

					<!-- Itens do Pedido -->
					<div class="receipt-items">
						<div class="receipt-item-row">
							<span>2x SMASH BACON DUPLO</span>
							<span>R$ 64,00</span>
						</div>
						<div class="item-sub">Obs: Carne ao ponto. Sem picles.</div>

						<div class="receipt-item-row">
							<span>1x BATATA RÚSTICA G</span>
							<span>R$ 18,00</span>
						</div>

						<div class="receipt-item-row">
							<span>2x COCA-COLA ZERO LATA</span>
							<span>R$ 14,00</span>
						</div>
					</div>

					<div class="divider-dash">--------------------------------</div>

					<!-- Totalizadores -->
					<div class="receipt-totals">
						<div class="tot-row">
							<span>Subtotal:</span>
							<span>R$ 96,00</span>
						</div>
						<div class="tot-row">
							<span>Taxa Entrega:</span>
							<span>R$ {store?.default_delivery_fee ? parseFloat(store.default_delivery_fee).toFixed(2).replace('.', ',') : '8,00'}</span>
						</div>
						<div class="tot-row main-total">
							<span>TOTAL:</span>
							<span>R$ 104,00</span>
						</div>
						<div class="tot-row">
							<span>Pagamento:</span>
							<span>PIX</span>
						</div>
					</div>

					<div class="divider-dash">--------------------------------</div>

					<!-- Chave e QR Code PIX -->
					<div class="pix-qr-area">
						{#if store?.pix_key}
							<div class="pix-type-label">
								CHAVE PIX ({store.pix_key_type || 'Chave'}):
							</div>
							<div class="pix-key-val">
								{store.pix_key}
							</div>
							{#if store?.pix_name}
								<div class="pix-payee">
									Favorecido: <strong>{store.pix_name}</strong>
								</div>
							{/if}
						{:else}
							<div class="qr-mock">
								<svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="#0F172A" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
									<rect x="3" y="3" width="7" height="7"></rect>
									<rect x="14" y="3" width="7" height="7"></rect>
									<rect x="14" y="14" width="7" height="7"></rect>
									<rect x="3" y="14" width="7" height="7"></rect>
								</svg>
								<span class="qr-label">QR CODE PIX</span>
							</div>
							<span class="qr-sub">Pagamento instantâneo via PIX</span>
						{/if}
					</div>

					<!-- Linha de Corte / Serrilha -->
					<div class="receipt-cut-line">
						<span>✂ - - - - - - CORTE AQUI - - - - - -</span>
					</div>
				</div>

				<!-- Serrilha Inferior -->
				<div class="receipt-edge bottom"></div>
			</div>
		</aside>
	</div>
</div>

<style>
	/* 1. Container Geral */
	.printer-page {
		display: flex;
		flex-direction: column;
		width: 100%;
		height: 100%;
		background: var(--color-neutral-50, #F8FAFC);
		padding: 24px 32px 48px;
		overflow-y: auto;
		box-sizing: border-box;
		color: var(--color-neutral-800, #1E293B);
	}

	/* 2. Header */
	.view-header {
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		margin-bottom: 24px;
		flex-wrap: wrap;
		gap: 16px;
	}

	.header-left {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.header-badge-row {
		display: flex;
		align-items: center;
		gap: 8px;
		flex-wrap: wrap;
	}

	.module-badge {
		font-size: 11px;
		font-weight: 800;
		letter-spacing: 0.6px;
		background: #FFF7ED;
		color: #C2410C;
		border: 1px solid #FFEDD5;
		padding: 2px 8px;
		border-radius: 6px;
	}

	.device-pill {
		display: inline-flex;
		align-items: center;
		gap: 5px;
		font-size: 11px;
		font-weight: 700;
		background: #F1F5F9;
		color: #475569;
		border: 1px solid #E2E8F0;
		padding: 2px 8px;
		border-radius: 6px;
	}

	.title-wrapper {
		display: flex;
		flex-direction: column;
		gap: 2px;
	}

	.view-title {
		font-size: 24px;
		font-weight: 800;
		color: var(--color-neutral-900, #0F172A);
		letter-spacing: -0.5px;
		margin: 0;
	}

	.view-subtitle {
		font-size: 13px;
		color: var(--color-neutral-500, #64748B);
		margin: 0;
		line-height: 1.4;
	}

	.header-actions {
		display: flex;
		align-items: center;
		gap: 12px;
		flex-wrap: wrap;
	}

	/* Status Pill */
	.server-status-pill {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 6px 14px;
		border-radius: 30px;
		background: #FFFFFF;
		border: 1px solid #E2E8F0;
		box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
	}

	.server-status-pill.online {
		border-color: #BBF7D0;
		background: #F0FDF4;
	}

	.server-status-pill.offline {
		border-color: #FECDD3;
		background: #FFF1F2;
	}

	.status-pulse-dot {
		width: 8px;
		height: 8px;
		border-radius: 50%;
		flex-shrink: 0;
	}

	.server-status-pill.online .status-pulse-dot {
		background: #10B981;
		box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2);
	}

	.server-status-pill.offline .status-pulse-dot {
		background: #EF4444;
		box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.2);
	}

	.status-text-group {
		display: flex;
		flex-direction: column;
	}

	.status-main {
		font-size: 12px;
		font-weight: 800;
		color: #0F172A;
		line-height: 1.2;
	}

	.status-sub {
		font-size: 10px;
		color: #64748B;
		line-height: 1.2;
	}

	/* Botão Atualizar */
	.btn-refresh {
		display: inline-flex;
		align-items: center;
		gap: 7px;
		background: #0F172A;
		color: #FFFFFF;
		padding: 8px 14px;
		border-radius: var(--radius-md, 10px);
		border: none;
		font-size: 12px;
		font-weight: 700;
		cursor: pointer;
		transition: all 0.15s ease;
		box-shadow: 0 2px 4px rgba(15, 23, 42, 0.12);
	}

	.btn-refresh:hover:not(:disabled) {
		background: #1E293B;
	}

	.btn-refresh:disabled {
		opacity: 0.7;
		cursor: not-allowed;
	}

	.icon-spin.active {
		animation: spin-rotate 0.9s linear infinite;
	}

	@keyframes spin-rotate {
		from { transform: rotate(0deg); }
		to { transform: rotate(360deg); }
	}

	/* 3. Grid Principal */
	.printer-grid {
		display: grid;
		grid-template-columns: minmax(0, 1fr) 420px;
		gap: 24px;
		flex: 1;
	}

	@media (max-width: 1080px) {
		.printer-grid {
			grid-template-columns: 1fr;
		}
	}

	.config-col {
		display: flex;
		flex-direction: column;
		gap: 20px;
	}

	/* 4. Cards */
	.card {
		background: #FFFFFF;
		border: 1px solid #E2E8F0;
		border-radius: var(--radius-lg, 14px);
		padding: 22px 24px;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
	}

	.card-title-row {
		display: flex;
		align-items: center;
		gap: 12px;
		margin-bottom: 20px;
	}

	.card-icon-box {
		width: 38px;
		height: 38px;
		border-radius: 10px;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}

	.card-icon-box.orange { background: #FFF7ED; color: #EA580C; }
	.card-icon-box.blue { background: #EFF6FF; color: #2563EB; }

	.card-title-row h3 {
		margin: 0;
		font-size: 15px;
		font-weight: 800;
		color: #0F172A;
		letter-spacing: -0.2px;
	}

	.card-desc {
		display: block;
		font-size: 12px;
		color: #64748B;
		margin-top: 2px;
	}

	.form-group {
		display: flex;
		flex-direction: column;
		gap: 8px;
		margin-bottom: 18px;
	}

	.field-label {
		font-size: 12px;
		font-weight: 700;
		color: #334155;
	}

	.select-wrapper {
		position: relative;
		display: flex;
		align-items: center;
	}

	.form-select {
		width: 100%;
		padding: 10px 38px 10px 14px;
		border-radius: var(--radius-md, 10px);
		border: 1px solid #CBD5E1;
		font-size: 13px;
		font-weight: 600;
		color: #0F172A;
		background: #FFFFFF;
		appearance: none;
		cursor: pointer;
		transition: border-color 0.15s ease, box-shadow 0.15s ease;
	}

	.form-select:focus {
		outline: none;
		border-color: #EA580C;
		box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.15);
	}

	.select-chevron {
		position: absolute;
		right: 14px;
		pointer-events: none;
		color: #64748B;
		display: flex;
		align-items: center;
	}

	.field-hint-box {
		margin-top: 4px;
	}

	.hint-badge {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		font-size: 12px;
		padding: 3px 8px;
		border-radius: 6px;
	}

	.hint-badge.connected {
		background: #F0FDF4;
		color: #166534;
		border: 1px solid #BBF7D0;
	}

	.hint-badge.warning {
		background: #FFFBEB;
		color: #92400E;
		border: 1px solid #FDE68A;
	}

	/* 5. Destinos de Mesa (Cards Interativos) */
	.destination-group {
		margin-top: 20px;
		padding-top: 18px;
		border-top: 1px dashed #E2E8F0;
	}

	.dest-cards-grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 10px;
	}

	@media (max-width: 640px) {
		.dest-cards-grid {
			grid-template-columns: 1fr;
		}
	}

	.dest-card {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 12px 14px;
		border-radius: var(--radius-md, 10px);
		border: 1px solid #E2E8F0;
		background: #FFFFFF;
		cursor: pointer;
		text-align: left;
		transition: all 0.15s ease;
	}

	.dest-card:hover {
		border-color: #CBD5E1;
		background: #F8FAFC;
	}

	.dest-card.active {
		border-color: #EA580C;
		background: #FFF7ED;
		box-shadow: 0 1px 3px rgba(234, 88, 12, 0.1);
	}

	.dest-radio-circle {
		width: 14px;
		height: 14px;
		border-radius: 50%;
		border: 2px solid #CBD5E1;
		flex-shrink: 0;
		transition: all 0.15s ease;
	}

	.dest-card.active .dest-radio-circle {
		border-color: #EA580C;
		background: #EA580C;
		box-shadow: inset 0 0 0 2px #FFFFFF;
	}

	.dest-content {
		display: flex;
		flex-direction: column;
	}

	.dest-title {
		font-size: 13px;
		font-weight: 800;
		color: #0F172A;
		line-height: 1.2;
	}

	.dest-desc {
		font-size: 11px;
		color: #64748B;
		margin-top: 2px;
	}

	.field-footnote {
		font-size: 11px;
		color: #64748B;
		margin-top: 6px;
		line-height: 1.4;
	}

	/* 6. Opções Operacionais & Switches */
	.highlight-option-card {
		display: flex;
		justify-content: space-between;
		align-items: center;
		background: #FFF7ED;
		border: 1px solid #FDBA74;
		border-radius: var(--radius-md, 12px);
		padding: 16px 18px;
		margin-bottom: 18px;
		gap: 16px;
	}

	.opt-title-line {
		display: flex;
		align-items: center;
		gap: 8px;
		flex-wrap: wrap;
	}

	.opt-main-title {
		font-size: 14px;
		font-weight: 800;
		color: #9A3412;
	}

	.tag-fast {
		font-size: 10px;
		font-weight: 800;
		background: #EA580C;
		color: #FFFFFF;
		padding: 2px 7px;
		border-radius: 4px;
		letter-spacing: 0.3px;
	}

	.opt-explanation {
		font-size: 12px;
		color: #C2410C;
		margin: 4px 0 0 0;
		line-height: 1.4;
	}

	.switches-list {
		display: flex;
		flex-direction: column;
		gap: 12px;
		margin-bottom: 22px;
	}

	.switch-row {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 12px 14px;
		background: #F8FAFC;
		border: 1px solid #E2E8F0;
		border-radius: 10px;
		gap: 14px;
		transition: background 0.12s ease;
	}

	.switch-row:hover {
		background: #F1F5F9;
	}

	.switch-info {
		display: flex;
		flex-direction: column;
		gap: 2px;
	}

	.switch-title {
		font-size: 13px;
		font-weight: 700;
		color: #0F172A;
	}

	.switch-desc {
		font-size: 11px;
		color: #64748B;
		line-height: 1.3;
	}

	/* Switch Slider */
	.switch-control {
		position: relative;
		display: inline-block;
		width: 40px;
		height: 22px;
		flex-shrink: 0;
	}

	.switch-control input {
		opacity: 0;
		width: 0;
		height: 0;
	}

	.slider {
		position: absolute;
		cursor: pointer;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-color: #CBD5E1;
		transition: 0.2s;
		border-radius: 22px;
	}

	.slider:before {
		position: absolute;
		content: "";
		height: 16px;
		width: 16px;
		left: 3px;
		bottom: 3px;
		background-color: white;
		transition: 0.2s;
		border-radius: 50%;
		box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
	}

	input:checked + .slider {
		background-color: #EA580C;
	}

	input:checked + .slider:before {
		transform: translateX(18px);
	}

	/* Botão de Teste */
	.test-action-box {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.btn-test-print {
		width: 100%;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 10px;
		background: #0F172A;
		color: #FFFFFF;
		padding: 12px 20px;
		border-radius: var(--radius-md, 10px);
		border: none;
		font-size: 13px;
		font-weight: 800;
		cursor: pointer;
		transition: all 0.15s ease;
		box-shadow: 0 2px 5px rgba(15, 23, 42, 0.15);
	}

	.btn-test-print:hover:not(:disabled) {
		background: #EA580C;
		transform: translateY(-1px);
		box-shadow: 0 4px 10px rgba(234, 88, 12, 0.25);
	}

	.btn-test-print:disabled {
		opacity: 0.7;
		cursor: not-allowed;
	}

	/* 7. Card Diagnóstico */
	.diagnostic-card {
		border-color: #E2E8F0;
		background: #FFFFFF;
	}

	.diag-header-row {
		display: flex;
		align-items: flex-start;
		gap: 12px;
		margin-bottom: 14px;
	}

	.diag-icon-box {
		width: 36px;
		height: 36px;
		border-radius: 8px;
		background: #FEF2F2;
		color: #DC2626;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}

	.diag-title-content h4 {
		margin: 0;
		font-size: 14px;
		font-weight: 800;
		color: #0F172A;
	}

	.diag-desc {
		font-size: 12px;
		color: #64748B;
		margin-top: 2px;
		line-height: 1.4;
	}

	.diag-action-row {
		display: flex;
		align-items: center;
	}

	.btn-repair {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		background: #FFFFFF;
		border: 1px solid #CBD5E1;
		color: #334155;
		padding: 9px 16px;
		border-radius: var(--radius-md, 8px);
		font-size: 12px;
		font-weight: 700;
		cursor: pointer;
		transition: all 0.15s ease;
	}

	.btn-repair:hover:not(:disabled) {
		background: #F8FAFC;
		border-color: #94A3B8;
		color: #0F172A;
	}

	.btn-repair:disabled {
		opacity: 0.7;
		cursor: not-allowed;
	}

	.repair-icon.spin {
		animation: spin-rotate 0.9s linear infinite;
	}

	/* Console de Log */
	.spooler-log-console {
		margin-top: 14px;
		background: #0F172A;
		border-radius: 8px;
		padding: 12px 14px;
		border: 1px solid #1E293B;
	}

	.console-header {
		display: flex;
		align-items: center;
		gap: 6px;
		margin-bottom: 8px;
		padding-bottom: 6px;
		border-bottom: 1px solid #1E293B;
	}

	.console-dot {
		width: 6px;
		height: 6px;
		border-radius: 50%;
		background: #10B981;
	}

	.console-title {
		font-size: 10px;
		font-weight: 700;
		letter-spacing: 0.5px;
		text-transform: uppercase;
		color: #94A3B8;
	}

	.console-text {
		margin: 0;
		font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
		font-size: 11px;
		line-height: 1.5;
		color: #F1F5F9;
		white-space: pre-wrap;
		word-break: break-all;
	}

	/* 8. Coluna de Prévia da Bobina Térmica */
	.preview-col {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 16px;
	}

	.preview-title-bar {
		display: flex;
		justify-content: space-between;
		align-items: center;
		width: 100%;
		max-width: 360px;
	}

	.preview-title-group {
		display: flex;
		flex-direction: column;
		gap: 2px;
	}

	.preview-tag {
		font-size: 10px;
		font-weight: 800;
		letter-spacing: 0.5px;
		color: #EA580C;
	}

	.preview-title-bar h3 {
		margin: 0;
		font-size: 15px;
		font-weight: 800;
		color: #0F172A;
	}

	.preview-live-badge {
		font-size: 11px;
		font-weight: 700;
		background: #F1F5F9;
		color: #475569;
		padding: 3px 8px;
		border-radius: 6px;
		border: 1px solid #E2E8F0;
	}

	/* Cupom Térmico Autêntico */
	.thermal-receipt-container {
		width: 100%;
		max-width: 360px;
		display: flex;
		flex-direction: column;
		filter: drop-shadow(0 12px 24px rgba(0, 0, 0, 0.08));
	}

	/* Serrilhado CSS */
	.receipt-edge {
		width: 100%;
		height: 8px;
		background: radial-gradient(circle, transparent, transparent 50%, #FFFFFF 50%, #FFFFFF 100%);
		background-size: 12px 12px;
	}

	.receipt-edge.top {
		background-position: bottom;
	}

	.receipt-edge.bottom {
		background-position: top;
		transform: rotate(180deg);
	}

	.thermal-receipt {
		background: #FFFFFF;
		padding: 16px 20px;
		font-family: 'Courier New', Courier, monospace;
		font-size: 12px;
		color: #111827;
		line-height: 1.35;
		letter-spacing: -0.2px;
	}

	.receipt-header {
		text-align: center;
	}

	.receipt-logo {
		font-size: 15px;
		font-weight: 900;
		letter-spacing: 0.5px;
		text-transform: uppercase;
		margin-bottom: 4px;
	}

	.receipt-info {
		font-size: 11px;
		color: #374151;
	}

	.divider-dash {
		margin: 6px 0;
		text-align: center;
		overflow: hidden;
		white-space: nowrap;
		color: #6B7280;
		letter-spacing: -1px;
	}

	.receipt-order-id {
		font-size: 15px;
		font-weight: 900;
		letter-spacing: 1px;
	}

	.receipt-date {
		font-size: 11px;
		color: #4B5563;
	}

	.receipt-client {
		font-size: 11px;
		display: flex;
		flex-direction: column;
		gap: 2px;
	}

	.receipt-items {
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	.receipt-item-row {
		display: flex;
		justify-content: space-between;
		font-weight: 700;
		font-size: 12px;
	}

	.item-sub {
		font-size: 10px;
		color: #4B5563;
		font-style: italic;
		padding-left: 6px;
	}

	.receipt-totals {
		display: flex;
		flex-direction: column;
		gap: 3px;
	}

	.tot-row {
		display: flex;
		justify-content: space-between;
		font-size: 11px;
	}

	.tot-row.main-total {
		font-size: 14px;
		font-weight: 900;
		margin: 2px 0;
	}

	.pix-qr-area {
		text-align: center;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 4px;
		padding: 4px 0;
	}

	.pix-type-label {
		font-size: 10px;
		font-weight: 700;
		color: #111827;
	}

	.pix-key-val {
		font-size: 12px;
		font-weight: 900;
		color: #EA580C;
		word-break: break-all;
	}

	.pix-payee {
		font-size: 10px;
		color: #4B5563;
	}

	.qr-mock {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 4px;
		padding: 8px 12px;
		border: 1px dashed #9CA3AF;
		border-radius: 4px;
	}

	.qr-label {
		font-size: 9px;
		font-weight: 800;
		letter-spacing: 0.5px;
	}

	.qr-sub {
		font-size: 10px;
		color: #4B5563;
	}

	.receipt-cut-line {
		margin-top: 10px;
		text-align: center;
		font-size: 10px;
		color: #6B7280;
		letter-spacing: 0.5px;
	}
</style>
